import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode
} from '../types'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'
import { tempTableName } from '../utils'

class CreateGrid extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.creategrid',
        group: 'Spatial Constructors',
        title: 'Create Grid',
        description: `
      This component creates a new table containing points distributed in a regular grid, according to a given interval.
      `,
        starred: true
      },
      [
        {
          name: 'table',
          type: WorkflowComponentParamType.InputTable,
          title: 'Reference table (used for extent)'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry],
          parent: 'table'
        },
        {
          name: 'interval',
          type: WorkflowComponentParamType.Number,
          title: 'Interval (degrees)',
          min: 0,
          max: 90,
          mode: WorkflowComponentParameterMode.Slider
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)
    let query = ''
    switch (connection.type) {
      case Provider.BigQuery: {
        const select = `WITH _bbox AS (
                          SELECT ST_EXTENT(${preparedParams.geo}) bbox
                          FROM \`${preparedParams.table}\`
                        )
                        SELECT ST_GEOGPOINT(lon, lat) geom
                        FROM _bbox,
                          UNNEST(GENERATE_ARRAY(bbox.xmin, bbox.xmax, ${preparedParams.interval})) AS lon,
                          UNNEST(GENERATE_ARRAY(bbox.ymin, bbox.ymax, ${preparedParams.interval})) AS lat`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
      case Provider.Snowflake: {
        const select = `WITH _envelope AS (
                          SELECT ST_COLLECT(${preparedParams.geo}) envelope
                          FROM ${preparedParams.table}
                        ), _bbox AS (
                            SELECT ST_XMIN(envelope) AS xmin,
                              ST_XMAX(envelope) AS xmax,
                              ST_YMIN(envelope) AS ymin,
                              ST_YMAX(envelope) AS ymax,
                              FLOOR((xmax-xmin)/ ${preparedParams.interval}) + 1 AS x_rows,
                              FLOOR((ymax-ymin)/ ${preparedParams.interval}) + 1 AS y_rows
                          FROM _envelope
                        ), _bbox_array AS (
                          SELECT xmin,
                            ymin,
                            SPLIT(lpad('', (x_rows - 1), '0'),'0') AS xrows_array,
                            SPLIT(lpad('', (y_rows - 1), '0'),'0') AS yrows_array
                          FROM _bbox
                          )
                        SELECT ST_POINT(xmin + xflatten.index * ${preparedParams.interval},
                          ymin + yflatten.index * ${preparedParams.interval}) geom
                        FROM _bbox_array,
                          lateral FLATTEN(input => xrows_array) as xflatten,
                          lateral FLATTEN(input => yrows_array) as yflatten`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
      case Provider.Postgres: {
        const select = `WITH _bbox AS (
                          SELECT ST_EXTENT(${preparedParams.geo}) bbox
                          FROM ${preparedParams.table}
                        )
                        SELECT ST_POINT(lon, lat) geom
                        FROM _bbox,
                            GENERATE_SERIES(ST_XMIN(bbox)::numeric, ST_XMAX(bbox)::numeric, ${preparedParams.interval}) AS lon,
                            GENERATE_SERIES(ST_YMIN(bbox)::numeric, ST_YMAX(bbox)::numeric, ${preparedParams.interval}) AS lat`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
      case Provider.Redshift: {
        const temptable = `${tempTableName(componentName, preparedParams.table)}`
        const select = `SELECT ST_POINT(x_min + x_index * ${preparedParams.interval},
          y_min + y_index * ${preparedParams.interval})
        FROM ${temptable} t, t.x_element as x AT x_index, t.y_element as y AT y_index`
        const createTable = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        query = dedent`
        BEGIN
          EXECUTE '
            CREATE TABLE ${temptable} AS
            WITH _envelope AS (
              SELECT ST_COLLECT(${preparedParams.geo}) envelope
              FROM ${preparedParams.table}
            ), _bbox AS (
              SELECT ST_XMIN(envelope) AS x_min,
                ST_XMAX(envelope) AS x_max,
                ST_YMIN(envelope) AS y_min,
                ST_YMAX(envelope) AS y_max
              FROM _envelope
            ), _nrows AS (
              SELECT x_min, y_min,
                FLOOR((x_max-x_min)/ ${preparedParams.interval})::INTEGER AS x_rows,
                FLOOR((y_max-y_min)/ ${preparedParams.interval})::INTEGER AS y_rows
              FROM _bbox
            )
            SELECT x_min, y_min,
              SPLIT_TO_ARRAY(LPAD ('''', x_rows, ''0''), ''0'') x_element,
              SPLIT_TO_ARRAY(LPAD ('''', y_rows, ''0''), ''0'') y_element
            FROM _nrows
          ';
          ${createTable}
          DROP TABLE ${temptable};
        END;`
        break
      }
      case Provider.DatabricksRest: {
        const select = `WITH _envelope AS (
                          SELECT ST_UNION_AGG(${connection.processInputGeogColumn(
                            preparedParams.geo as string
                          )}) envelope
                          FROM ${preparedParams.table}
                        ), _bbox AS (
                            SELECT CAST(ST_XMIN(envelope) AS INT) AS xmin,
                              CAST(ST_XMAX(envelope) AS INT) AS xmax,
                              CAST(ST_YMIN(envelope) AS INT) AS ymin,
                              CAST(ST_YMAX(envelope) AS INT) AS ymax
                          FROM _envelope
                        ), _grid AS (
                          SELECT
                            EXPLODE(SEQUENCE(xmin, xmax, ${preparedParams.interval})) AS lon,
                            EXPLODE(SEQUENCE(ymin, ymax, ${preparedParams.interval})) AS lat
                          FROM _bbox
                        )
                        SELECT ${connection.processOutputGeogColumn(`ST_POINT(lon, lat)`)} geom
                        FROM _grid`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
      default:
        return null
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ connection }: GetOutputSchemaParams): TableSchema {
    return [
      {
        name: 'geom',
        type: connection.getGeoDataType()
      }
    ]
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Postgres, Provider.Redshift, Provider.Snowflake, Provider.DatabricksRest]
  }
}

export { CreateGrid }
